import { useEffect, useId, useRef, useState, type ReactNode, type FormEvent } from 'react';

// Modal + ConfirmDialog. Branded replacements for window.prompt/confirm: consequences are
// stated, destructive actions carry the danger treatment, an optional reason is captured
// inline, and Esc/backdrop close. Reversible actions shouldn't use these at all — they
// should act immediately and offer Undo via a toast.

export function Modal({
  open,
  onClose,
  title,
  children,
  width = 440,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    addEventListener('keydown', onKey);
    // Move focus into the dialog so keyboard users land inside it.
    const first = ref.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    return () => removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ width }} ref={ref}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          <button className="modal-x" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export interface ConfirmSpec {
  title: string;
  /** What will happen, in plain words — including what cannot be undone. */
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  /** Ask for a reason; when 'required', confirm stays disabled until one is typed. */
  reason?: 'optional' | 'required';
  reasonLabel?: string;
}

export function ConfirmDialog({
  open,
  spec,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  spec: ConfirmSpec;
  busy?: boolean;
  onConfirm: (reason: string | undefined) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const needsReason = spec.reason === 'required' && !reason.trim();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy || needsReason) return;
    onConfirm(reason.trim() || undefined);
  };

  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} title={spec.title}>
      <form onSubmit={submit} className="stack">
        <div className="modal-copy">{spec.body}</div>
        {spec.reason && (
          <div className="field">
            <label htmlFor="confirm-reason">
              {spec.reasonLabel ?? 'Reason'}
              {spec.reason === 'optional' && <span className="muted"> (optional)</span>}
            </label>
            <input
              id="confirm-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={spec.reason === 'required' ? 'Required' : ''}
              autoFocus
            />
          </div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className={`btn ${spec.danger ? 'danger' : 'primary'}`} disabled={busy || needsReason}>
            {busy ? 'Working…' : spec.confirmLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
}
